import { createIntl, createIntlCache, type IntlShape, ReactIntlErrorCode } from "react-intl";

/**
 * Used until `bindInsightsIntl` runs, and by tests that render no provider. It carries no messages, so every
 * lookup falls back to the descriptor's `defaultMessage`.
 */
const fallbackIntl = createIntl(
  {
    locale: "en",
    messages: {},
    // Falling back to `defaultMessage` is the expected path here, not an error. Without this every lookup logs a
    // MISSING_TRANSLATION error to the console.
    onError: (error) => {
      if (error.code !== ReactIntlErrorCode.MISSING_TRANSLATION) {
        throw error;
      }
    },
  },
  createIntlCache()
);

let boundIntl: IntlShape = fallbackIntl;

/**
 * Points `insightsIntl` at the app's real `intl`, so this section's copy is translated like the rest of the app.
 *
 * Call once, above anything that renders an insights screen. The app has no runtime language switcher, so one
 * binding at startup is enough; messages are loaded asynchronously, which is why this cannot simply be built at
 * import time.
 */
export const bindInsightsIntl = (intl: IntlShape) => {
  boundIntl = intl;
};

/**
 * An `intl` for insights' non-React code.
 *
 * Most of this section formats its copy in module-scope functions and import-time constants, which cannot reach the
 * `intl` from React context. Rather than thread an `IntlShape` through the whole call graph, those call sites use
 * this. It forwards to whatever `bindInsightsIntl` was given, so lookups resolve against the real messages.
 *
 * TODO: move these call sites onto `useIntl()` and delete this. It works only because the locale is fixed for the
 * lifetime of the page; a language switcher would need the React context to re-render on change.
 */
export const insightsIntl = new Proxy({} as IntlShape, {
  get: (_target, property) => boundIntl[property as keyof IntlShape],
});
